import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from "@mui/material";
import "react-dropzone-uploader/dist/styles.css";
import { getDateInDays, getDaysAfter, sendGetRequest } from "../utils/utils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import EditPage from "./component/edit-page";
import React, { useState, useEffect } from "react";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Sector,
} from "recharts";
import CustomTooltip from "./component/tooltip";

const Analytics = () => {
  const [startDate, setStartDate] = useState(getDateInDays(30));
  const [endDate, setEndDate] = useState(getDaysAfter(1));
  const [graphs, setGraphs] = useState({});
  const onScreenGraphHeight = 400;

  const [status, setStatus] = useState({
    error: false,
    loading: false,
    message: null,
  });

  const COLORS = [
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7400",
    "#154038",
    "#694c0c",
    "#672403",
  ];

  useEffect(() => {
    setStatus({ ...{ loading: true } });
    let url = `analytics/playroom-summary?application=playroom-app`;

    if (startDate && endDate) {
      url += `&startDate=${startDate.getTime()}&endDate=${endDate.getTime()}`;
    }

    sendGetRequest(
      url,
      (loading) => {
        setStatus({ ...{ loading }, ...loading });
      },
      (data) => {
        if (data) setGraphs(data);
      },
    );
  }, [startDate, endDate]);

  // Expandable Card Wrapper Component
  const ExpandableCard = ({ title, children, expandedHeight = 600 }) => {
    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    // Clone the children with new height for expanded view
    const expandedChildren = React.Children.map(children, (child) => {
      if (child.type === ResponsiveContainer) {
        return React.cloneElement(child, { height: expandedHeight, open: open });
      }
      return child;
    });

    return (
      <>
        <Grid item md={6} xs={12}>
          <Card className="shadow-md rounded-2xl">
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">{title}</Typography>
                <IconButton
                  onClick={handleOpen}
                  size="small"
                  aria-label="expand"
                >
                  <FullscreenIcon />
                </IconButton>
              </Box>
              {children}
            </CardContent>
          </Card>
        </Grid>

        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            style: {
              height: "90vh",
            },
          }}
        >
          <DialogTitle>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5">{title} - Expanded View</Typography>
              <IconButton onClick={handleClose} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box height="100%" width="100%">
              {expandedChildren}
            </Box>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  // 1. New vs Returning Users Chart
  const NewReturningUsersChart = ({ graphs, height = 400 }) => {
    if (!graphs?.newReturning?.length) return null;

    return (
      <ExpandableCard title="New vs Returning Users Over Time">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={graphs.newReturning}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line
              type="monotone"
              dataKey="new"
              stroke={COLORS[0]}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="returning"
              stroke={COLORS[1]}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 2. Retention Cohort Chart
  const RetentionCohortChart = ({ graphs, height = 400 }) => {
    if (!graphs?.retention?.length) return null;

    return (
      <ExpandableCard title="Retention Cohort">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={graphs.retention}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="signupWeek" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line dataKey="w0" stroke={COLORS[0]} strokeWidth={2} />
            <Line dataKey="w1" stroke={COLORS[1]} strokeWidth={2} />
            <Line dataKey="w2" stroke={COLORS[2]} strokeWidth={2} />
            <Line dataKey="w3" stroke={COLORS[3]} strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 3. Unique Users by Language Chart
  const LanguageUsersChart = ({ graphs, height = 400 }) => {
    if (!graphs?.adaptedLanguage?.length) return null;

    return (
      <ExpandableCard title="Unique Users by Language & Geography">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={graphs.adaptedLanguage}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="language" />
            <YAxis />
            <Tooltip content={CustomTooltip}/>
            {Object.keys(graphs.adaptedLanguage[0])
              .filter((k) => k !== "language")
              .map((country, idx) => (
                <Bar
                  key={country}
                  dataKey={country}
                  stackId="a"
                  fill={COLORS[idx % COLORS.length]}
                  name={country}
                />
              ))}
          </BarChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 4. Content Completion Chart
  const ContentCompletionChart = ({ graphs, height = 400 }) => {
    if (!graphs?.contentCompletion?.length) return null;

    return (
      <ExpandableCard title="Content Completion Rate">
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={graphs.contentCompletion}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="content" hide={true} />
            <YAxis tickFormatter={(val) => `${(val * 100).toFixed(0)}%`} />
            <Tooltip formatter={(val) => `${(val * 100).toFixed(1)}%`} />
            <Legend />
            <Line
              type="monotone"
              dataKey="completion"
              stroke={COLORS[0]}
              strokeWidth={2}
              name="Completion State"
            />
          </LineChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 5. Multi-Show Engagement Chart
  const ShowEngagementChart = ({ graphs, height = 400 }) => {
    if (!graphs?.showEngagement?.length) return null;

    return (
      <ExpandableCard title="Multi-Show Engagement">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={graphs.showEngagement} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="show" type="category" hide={true} />
            <Tooltip />
            <Legend />
            <Bar dataKey="users" fill={COLORS[4]} />
          </BarChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 6. Repeat Engagement Chart
  const RepeatEngagementChart = ({ graphs, height = 400 }) => {
    if (
      !graphs?.repeatEngagement?.length ||
      graphs.repeatEngagement.length <= 1
    )
      return null;

    return (
      <ExpandableCard title="Repeat Engagement with Learning Content">
        <ResponsiveContainer width="100%" height={height}>
          <AreaChart data={graphs.repeatEngagement}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" hide={true} />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="sessions"
              fill={COLORS[5]}
              stroke={COLORS[6]}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 7. Challenges Attempts by Day Chart
  const ChallengesByDayChart = ({ graphs, height = 400 }) => {
    if (
      !graphs?.challenges?.attemptsByDay?.length ||
      graphs.challenges.attemptsByDay.length <= 1
    )
      return null;

    const processedData = graphs.challenges.attemptsByDay.map((data) => {
      console.log("Data point for tooltip:", data);
      return ({
      ...data,
      title: `${data.title} (Users - ${data.users}, CR - ${data.completionRate})`,
    })
    });

    return (
      <ExpandableCard
        title={`Overall Challenges Attempts (Completion rate (CR) of ${graphs.challenges.overallCompletionRate})`}
      >
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={processedData}
            margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="title"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
              hide={true}
              tick={{ fontSize: 11 }}
            />
            <YAxis width={45} />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="value"
              fill={COLORS[0]}
              activeBar={{ fill: COLORS[1], stroke: COLORS[1] }}
              radius={[10, 10, 0, 0]}
              name="User Challenge Attempts"
            />
          </BarChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 8. Challenges by Location Chart
  const ChallengesByLocationChart = ({ graphs, height = 400 }) => {
    if (
      !graphs?.challenges?.locations?.length ||
      graphs.challenges.locations.length <= 1
    )
      return null;

    const processedData = graphs.challenges.locations.map((data) => ({
      ...data,
      name: `${data.name} (Users - ${data.users}, AT - ${data.attempts}, CR - ${data.completionRate})`,
    }));

    return (
      <ExpandableCard title="Overall Challenges Attempts by User Location">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={processedData}
            margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={60}
              interval={0}
              hide={true}
              tick={{ fontSize: 11 }}
            />
            <YAxis width={45} hide={false} />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="attempts"
              fill={COLORS[5]}
              activeBar={{ fill: COLORS[4], stroke: COLORS[4] }}
              radius={[10, 10, 0, 0]}
              name="User Challenge Attempts"
            />
          </BarChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  // 9. Challenges by Content Type Chart - Pie Chart with Active Shape
  const TrackerByContentTypeChart = ({ graphs, height = 400, open = false }) => {
    const [activeIndex, setActiveIndex] = useState(1);

    if (
      !graphs?.challenges?.attemptsByContentType?.length ||
      graphs.challenges.attemptsByContentType.length <= 0
    ) {
      return null;
    }


    const onPieEnter = (_, index) => {
      setActiveIndex(index);
    };

    const renderActiveShape = ({
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
    }) => {
      const RADIAN = Math.PI / 180;
      const sin = Math.sin(-RADIAN * (midAngle ?? 1));
      const cos = Math.cos(-RADIAN * (midAngle ?? 1));
      const sx = (cx ?? 0) + ((outerRadius ?? 0) + 10) * cos;
      const sy = (cy ?? 0) + ((outerRadius ?? 0) + 10) * sin;
      const mx = (cx ?? 0) + ((outerRadius ?? 0) + 30) * cos;
      const my = (cy ?? 0) + ((outerRadius ?? 0) + 30) * sin;
      const ex = mx + (cos >= 0 ? 1 : -1) * 22;
      const ey = my;
      const textAnchor = cos >= 0 ? "start" : "end";

      const mappedContentTypes = {
        gamesPlayed: { label: "Games Played", main: `# games played by ## users` },
        booksRead: { label: "Books Read", main: `# books read by ## users` },
        musicListened: { label: "Music Listened", main: `# songs listened by ## users` },
        videoPlayed: { label: "Videos Played", main: `# videos played by ## users` },
      }

      return (
        <g>
          <text x={cx} y={cy} dy={8} textAnchor="middle" fill={fill}>
            {mappedContentTypes[payload.type]?.label || payload.type}
          </text>
          <Sector
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={startAngle}
            endAngle={endAngle}
            fill={fill}
          />
          <Sector
            cx={cx}
            cy={cy}
            startAngle={startAngle}
            endAngle={endAngle}
            innerRadius={(outerRadius ?? 0) + 6}
            outerRadius={(outerRadius ?? 0) + 10}
            fill={fill}
          />
          <path
            d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
            stroke={fill}
            fill="none"
          />
          <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
          <text
            x={ex + (cos >= 0 ? 1 : -1) * 12}
            y={ey}
            textAnchor={textAnchor}
            fill="#333"
          >{`${mappedContentTypes[payload.type]?.main.replace('#', payload.contentAttempted).replace("##", payload.uniqueUsers)}`}</text>
          <text
            x={ex + (cos >= 0 ? 1 : -1) * 12}
            y={ey}
            dy={18}
            textAnchor={textAnchor}
            fill="#999"
          >
            {`Completion rate: ${payload.contentCompletedRate}`}
          </text>
        </g>
      );
    };

    return (
      <ExpandableCard
        title="Most interacted content types"
        expandedHeight={height * 1.5}
      >
        <ResponsiveContainer width="100%" height={height}>
          <PieChart
            margin={{
              top: 50,
              right: 120,
              bottom: 0,
              left: 120,
            }}
          >
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={graphs.challenges.attemptsByContentType}
              cx="50%"
              cy="50%"
              innerRadius={open ? "70%" : "40%"}
              outerRadius={open ? "90%" : "60%"}
              fill={COLORS[1]}
              dataKey="contentAttempted"
              onMouseEnter={onPieEnter}
              isAnimationActive={true}
            />
            <Tooltip content={() => null} />
          </PieChart>
        </ResponsiveContainer>
      </ExpandableCard>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en-US">
      <EditPage
        title={"Analytics Dashboard"}
        status={status}
        showBtn={false}
        validated={false}
        backgroundColor={"#F5F5F5"}
        cardProps={{ elevation: 0 }}
        createNewElement={
          <Box
            sx={{ display: "flex", flexDirection: "row", mb: 4, mr: 2, gap: 2 }}
          >
            <DatePicker
              label="From Date"
              value={startDate}
              onChange={(newValue) => {
                setStartDate(new Date(newValue.toLocaleString()));
              }}
              renderInput={(params) => (
                <TextField {...params} sx={{ minWidth: 150 }} />
              )}
              disabled={status.loading}
            />

            <div
              style={{
                height: "3px",
                backgroundColor: "black",
                width: "10px",
                margin: "25px auto",
              }}
            />

            <DatePicker
              label="To Date"
              value={endDate}
              onChange={(newValue) => {
                setEndDate(new Date(newValue.toLocaleString()));
              }}
              renderInput={(params) => (
                <TextField {...params} sx={{ minWidth: 150 }} />
              )}
              disabled={status.loading}
              minDate={startDate}
            />
          </Box>
        }
        onFinish={() => {}}
      >
        <ChallengesByDayChart graphs={graphs} height={onScreenGraphHeight} />
        <ChallengesByLocationChart
          graphs={graphs}
          height={onScreenGraphHeight}
        />
        <TrackerByContentTypeChart
          graphs={graphs}
          height={onScreenGraphHeight}
        />
        <LanguageUsersChart graphs={graphs} height={onScreenGraphHeight} />
        <NewReturningUsersChart graphs={graphs} height={onScreenGraphHeight} />
        <ContentCompletionChart graphs={graphs} height={onScreenGraphHeight} />
        <ShowEngagementChart graphs={graphs} height={onScreenGraphHeight} />
        <RepeatEngagementChart graphs={graphs} height={onScreenGraphHeight} />
        <RetentionCohortChart graphs={graphs} height={onScreenGraphHeight} />
      </EditPage>
    </LocalizationProvider>
  );
};

export default Analytics;
